import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  BookOpenCheck, ClipboardCheck, Gauge, MessageSquare, RefreshCw, X,
} from 'lucide-react';
import { getTeacherStudentProgress } from '../../api/teacher';
import { sendDirectMessage, getMessageConversation } from '../../api/messages';
import { getRoomMessages } from '../../api/chat';
import { getTokenFromStorage, parseJwt } from '../../api/client';
import toast from 'react-hot-toast';
import { errorText } from '../../utils/errorText';
import { failure } from './apiError';
import { useI18n } from '../../i18n/I18nContext';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import MetricCard from '../../components/MetricCard/MetricCard';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import TableCard from '../../components/TableCard/TableCard';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import PageHead from '../../components/PageHead/PageHead';
import Modal from '../../components/Modal/Modal';
import { useMediaQuery } from '../../components/Layout/shellState';
import { useCourseTitle } from '../../hooks/useCourseTitle';
import usePageTitle from '../../hooks/usePageTitle';
import StudentPresenceHeader from '../../components/Student360/StudentPresenceHeader';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import {
  BAR_CURSOR_PROPS, ChartFrame, ChartTooltip, GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, barProps,
} from '../../components/charts/chartTheme';
import {
  formatDate, formatDateTime, formatPercent, formatShortDateTime, formatTime, todayYmd, toYmd,
} from '../../utils/datetime';
import './StudentDetail.css';

const HISTORY_PAGE_SIZE = 100; // the backend's per-page maximum, for both message stores
const MAX_DIRECT_MESSAGE_PAGES = 10;
const MAX_HISTORY_MESSAGES = 200;
// A teacher's message is stored as a DirectMessage and copied to the chat room at once; the two
// copies are matched on sender + text within this window.
const MIRROR_WINDOW_MS = 2 * 60 * 1000;

/** The signed-in user's id, from the access token. */
function currentUserId() {
  try {
    const token = getTokenFromStorage('accessToken');
    return (token && parseJwt(token)?.sub) || null;
  } catch {
    return null;
  }
}

/** A DM room is named after both people, sorted (ChatService.getCanonicalDMRoomId). */
function dmRoomId(userA, userB) {
  return `dm:${[userA, userB].sort().join(':')}`;
}

// GET /messages/direct/:id lists the oldest first and has no "newest" option, so read all its pages.
async function fetchDirectMessages(studentId) {
  const messages = [];
  for (let page = 1; page <= MAX_DIRECT_MESSAGE_PAGES; page += 1) {
    const result = await getMessageConversation(studentId, { page, limit: HISTORY_PAGE_SIZE });
    const list = Array.isArray(result) ? result : result.data || [];
    messages.push(...list);
    if (list.length < HISTORY_PAGE_SIZE) break;
  }
  return messages;
}

async function fetchRoomMessages(teacherId, studentId) {
  if (!teacherId) return [];
  const result = await getRoomMessages(dmRoomId(teacherId, studentId), { limit: HISTORY_PAGE_SIZE });
  return (result?.messages || []).filter((message) => !message.deletedAt);
}

/**
 * The conversation with a student, oldest first. It lives in two places: DirectMessage rows
 * (what this panel has always read) and the chat DM room, where the student reads a teacher's
 * message and answers it — a student has no other way to reply, so their answers are only there.
 * What the teacher sent is in both, so a DirectMessage with a twin in the room is dropped.
 */
async function loadConversation(studentId) {
  const [direct, room] = await Promise.allSettled([
    fetchDirectMessages(studentId),
    fetchRoomMessages(currentUserId(), studentId),
  ]);
  if (direct.status === 'rejected' && room.status === 'rejected') throw direct.reason;
  const directMessages = direct.status === 'fulfilled' ? direct.value : [];
  const roomMessages = room.status === 'fulfilled' ? room.value : [];

  const twins = roomMessages.map((message) => ({
    senderId: message.senderId,
    text: (message.content || '').trim(),
    at: new Date(message.createdAt).getTime(),
    used: false,
  }));
  const unmirrored = directMessages.filter((message) => {
    const text = (message.body || message.content || '').trim();
    const at = new Date(message.sentAt || message.createdAt).getTime();
    const twin = twins.find((candidate) => (
      !candidate.used
      && candidate.senderId === message.senderId
      && candidate.text === text
      && Math.abs(candidate.at - at) <= MIRROR_WINDOW_MS
    ));
    if (twin) twin.used = true;
    return !twin;
  });

  return [...unmirrored, ...roomMessages]
    .map((message) => ({
      id: message.id,
      senderId: message.senderId,
      content: message.content ?? message.body ?? '',
      hasAttachment: Array.isArray(message.attachments) && message.attachments.length > 0,
      createdAt: message.sentAt || message.createdAt,
    }))
    .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
    .slice(-MAX_HISTORY_MESSAGES);
}

/** Passed / failed for a lesson-quiz attempt; legacy rows without it fall back to their status. */
function attemptOutcome(attempt, t) {
  if (typeof attempt.passed === 'boolean') {
    return attempt.passed
      ? { label: t('teacher.studentDetail.passed'), tone: 'success' }
      : { label: t('teacher.studentDetail.failed'), tone: 'danger' };
  }
  return attempt.status === 'COMPLETED'
    ? { label: t('teacher.studentDetail.completed'), tone: 'success' }
    : { label: t('teacher.studentDetail.inProgress'), tone: 'info' };
}

// A chart earns its place with enough points to show a shape: a course of three lessons or a pair
// of quizzes reads better in the table and the list below, which carry the same numbers.
const MIN_LESSON_CHART_POINTS = 8;
const MIN_QUIZ_CHART_POINTS = 3;

/** Today's messages show the time; an older one says the day too ("15-okt, 21:16"). */
function messageStamp(value, lang) {
  if (!value) return '';
  return toYmd(value) === todayYmd() ? formatTime(value, lang) : formatShortDateTime(value, lang);
}

// A quiz's name under its bar, cut to what fits: shorter on a phone, where four bars share ~300px
// (and with the chat panel open the chart beside it is narrow too). The formatter takes ONE
// argument on purpose: Recharts calls tickFormatter(value, index), and a second parameter would
// take the index.
const quizTick = (limit) => (text) => {
  const value = String(text ?? '');
  return value.length > limit ? `${value.slice(0, limit - 1)}…` : value;
};

/**
 * Story 9.2-b: Individual Student Progress — darsly-crm Integration
 *
 * Wires GET /teacher/courses/:courseId/students/:studentId
 * Backend returns StudentProgressDetailDto:
 *   courseId, studentId, studentName, studentEmail, overallProgress,
 *   enrolledAt, completedAt, lessonProgress[], quizAttempts[],
 *   avgQuizScore, lessonsCompleted, totalLessons
 */
function StudentDetail() {
  const { studentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [searchParams] = useSearchParams();
  // Prefer the query string so the page survives a refresh/bookmark/shared link;
  // location.state (set by an in-app "View" click) is only a fallback for the
  // click-through case, since it does not survive a hard reload.
  const courseId = searchParams.get('courseId') || location.state?.courseId;
  // The course the numbers belong to, over the student's name (the generic parent label until it is known)
  const courseTitle = useCourseTitle(courseId);
  // Below 1024px the conversation is a sheet over the page; wider it is a panel beside it
  const compact = useMediaQuery('(max-width: 1023px)', false);
  const phone = useMediaQuery('(max-width: 639px)', false);

  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // The browser tab and the last top-bar crumb carry the student's name once it is known; until
  // then the route's generic title ("O'quvchi tafsiloti") stands in.
  usePageTitle((student && (student.studentName || student.name)) || t('topbar.title.studentDetail'));

  // Direct message compose panel
  const [msgPanelOpen, setMsgPanelOpen] = useState(false);
  const [msgContent, setMsgContent] = useState('');
  const [msgSending, setMsgSending] = useState(false);
  const [msgError, setMsgError] = useState(null);
  const [msgHistory, setMsgHistory] = useState([]);
  const [msgHistoryLoading, setMsgHistoryLoading] = useState(false);
  const msgHistoryRef = useRef(null);
  const msgTextRef = useRef(null);

  const fetchStudentProgress = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getTeacherStudentProgress(courseId, studentId);
      setStudent(data);
      setError(null);
      setNotFound(false);
    } catch (err) {
      // A student that is not (or no longer) in this course is "not found", not a failure to retry
      const gone = (err?.code ?? err?.response?.status) === 404;
      setNotFound(gone);
      setError(gone ? null : failure(err, t('teacher.studentDetail.failedToLoad')));
    } finally {
      setLoading(false);
    }
  }, [courseId, studentId, t]);

  useEffect(() => {
    if (!courseId) {
      // Neither the ?courseId= query string nor location.state carried a course —
      // this is a genuinely cold link with no course context. Notify before redirecting.
      console.warn(
        'StudentDetail: courseId missing from both the URL query string and location.state.'
      );
      toast.error(t('teacher.studentDetail.courseContextMissing'));
      navigate('/teacher/dashboard');
      return;
    }
    fetchStudentProgress();
  }, [studentId, courseId, navigate, fetchStudentProgress, t]);

  // Show the newest messages: the history opens (and grows) at its bottom.
  useEffect(() => {
    const el = msgHistoryRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgHistory, msgHistoryLoading, msgPanelOpen]);

  // Beside the page the panel opens with the cursor in the message box (the sheet does this itself).
  useEffect(() => {
    if (msgPanelOpen && !compact && msgTextRef.current) msgTextRef.current.focus({ preventScroll: true });
  }, [msgPanelOpen, compact]);

  const openMessagePanel = async () => {
    setMsgPanelOpen(true);
    setMsgHistoryLoading(true);
    setMsgError(null);
    try {
      setMsgHistory(await loadConversation(studentId));
    } catch {
      setMsgHistory([]);
    } finally {
      setMsgHistoryLoading(false);
    }
  };

  const closeMessagePanel = () => {
    setMsgPanelOpen(false);
    setMsgContent('');
    setMsgError(null);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!msgContent.trim()) return;

    setMsgSending(true);
    setMsgError(null);
    try {
      await sendDirectMessage({ recipientId: studentId, content: msgContent.trim(), courseId });
      setMsgContent('');
      // H-3: wrap history re-fetch in try/catch — message was sent, stale history is non-fatal
      try {
        setMsgHistory(await loadConversation(studentId));
      } catch {
        setMsgError(t('teacher.studentDetail.messageSentRefresh'));
      }
    } catch (err) {
      setMsgError(errorText(err, t('teacher.studentDetail.failedToSendMessage')));
    } finally {
      setMsgSending(false);
    }
  };

  const backToDashboard = () => navigate('/teacher/dashboard');
  const back = { onClick: backToDashboard, label: t('teacher.studentDetail.backToDashboard') };
  const eyebrow = courseTitle || t('teacher.studentDetail.parentName');

  if (loading && !student) {
    // The header is already there (a way back, the page's one h1); the tiles and the table are bones
    return (
      <div className="student-detail">
        <PageHead back={back} eyebrow={eyebrow} title={t('teacher.studentDetail.pageTitle')} />
        <div className="ui-stack ui-stack--lg">
          <div className="kpi-row tsd-kpis" aria-busy="true">
            {[0, 1, 2].map((i) => <MetricCard key={i} loading />)}
          </div>
          <LoadingState message={t('teacher.studentDetail.loading')} skeleton="table" rows={4} />
        </div>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="student-detail">
        <PageHead back={back} eyebrow={eyebrow} title={t('teacher.studentDetail.pageTitle')} />
        {notFound ? (
          <PageEmptyState
            variant="search"
            title={t('teacher.studentDetail.notFound')}
            description={t('teacher.studentDetail.notFoundDesc')}
            actionLabel={t('teacher.studentDetail.backToDashboard')}
            onAction={backToDashboard}
          />
        ) : (
          <PageEmptyState
            variant="error"
            title={error?.title || t('teacher.studentDetail.failedToLoad')}
            description={error?.detail}
            onRetry={fetchStudentProgress}
          />
        )}
      </div>
    );
  }

  // One bar per lesson: how far the student is through it
  const lessonsData = student.lessonProgress || [];
  const progressData = lessonsData.map((lp, idx) => ({
    n: idx + 1,
    title: lp.title || t('teacher.studentDetail.lessonFallbackTitle', { number: idx + 1 }),
    progress: lp.completionPercent || (lp.completed ? 100 : 0),
  }));

  // Each unique quiz gets its latest score shown as a bar
  const quizScoreMap = {};
  (student.quizAttempts || []).forEach((attempt) => {
    const key = attempt.quizId || attempt.assignmentId || attempt.attemptId;
    if (!quizScoreMap[key] || (attempt.attemptedAt && (!quizScoreMap[key].attemptedAt || new Date(attempt.attemptedAt) > new Date(quizScoreMap[key].attemptedAt)))) {
      quizScoreMap[key] = attempt;
    }
  });
  const quizData = Object.values(quizScoreMap).map((attempt) => ({
    quiz: attempt.title || t('teacher.studentDetail.quizFallback'),
    score: attempt.totalScore > 0 ? Math.round((attempt.score / attempt.totalScore) * 100) : 0,
  }));
  const showLessonChart = progressData.length >= MIN_LESSON_CHART_POINTS;
  const showQuizChart = quizData.length >= MIN_QUIZ_CHART_POINTS;

  const displayName = student.studentName || student.name;
  const displayEmail = student.studentEmail || student.email;
  const percentTick = (value) => `${value}%`;
  const panelTitle = t('teacher.studentDetail.messagePanelTitle', { name: displayName });

  // The conversation: what was said, a line for a failure, the box to write in. The same markup is
  // the panel beside the page and the body of the sheet below 1024px.
  const conversation = (
    <>
      <div className="message-history" ref={msgHistoryRef}>
        {msgHistoryLoading ? (
          <LoadingState compact message={t('teacher.studentDetail.loadingHistory')} />
        ) : msgHistory.length === 0 ? (
          <p className="msg-empty">{t('teacher.studentDetail.noPreviousMessages')}</p>
        ) : (
          msgHistory.map((msg, idx) => (
            <div
              key={msg.id || idx}
              className={`msg-bubble ${msg.senderId === studentId ? 'msg-received' : 'msg-sent'}`}
            >
              <p>{msg.content || (msg.hasAttachment ? t('teacher.studentDetail.attachmentMessage') : '')}</p>
              <span className="msg-time">{messageStamp(msg.createdAt, lang)}</span>
            </div>
          ))
        )}
      </div>

      {msgError && (
        <div className="ui-alert tsd-msg-alert" role="alert">
          <p>{msgError}</p>
        </div>
      )}

      <form className="message-compose" onSubmit={handleSendMessage}>
        <textarea
          ref={msgTextRef}
          data-autofocus
          value={msgContent}
          onChange={(e) => setMsgContent(e.target.value)}
          placeholder={t('teacher.studentDetail.messagePlaceholder')}
          aria-label={t('teacher.studentDetail.messagePlaceholder')}
          maxLength={2000}
          rows={3}
        />
        <button
          type="submit"
          className={`ds-btn ds-btn-primary${msgSending ? ' is-loading' : ''}`}
          disabled={msgSending || !msgContent.trim()}
          aria-busy={msgSending || undefined}
        >
          {t('teacher.studentDetail.send')}
        </button>
      </form>
    </>
  );

  return (
    <div className="student-detail">
      <PageHead
        className="tsd-head"
        back={back}
        eyebrow={eyebrow}
        title={displayName}
        description={(
          <span className="tsd-meta">
            {displayEmail && <span>{displayEmail}</span>}
            {student.enrolledAt && (
              <span>{t('teacher.studentDetail.enrolled')}: {formatDate(student.enrolledAt, lang)}</span>
            )}
            {student.completedAt && (
              <span>{t('teacher.studentDetail.completedOn', { date: formatDate(student.completedAt, lang) })}</span>
            )}
          </span>
        )}
        actions={(
          <>
            <button
              type="button"
              className={`ds-btn ds-btn-ghost ds-btn-icon tsd-act-refresh${loading ? ' is-loading' : ''}`}
              onClick={fetchStudentProgress}
              disabled={loading}
              aria-label={t('teacher.studentDetail.refreshData')}
              title={t('teacher.studentDetail.refreshData')}
            >
              <RefreshCw aria-hidden="true" />
            </button>
            <button
              type="button"
              className="ds-btn ds-btn-secondary tsd-act-secondary"
              onClick={() => navigate(`/teacher/students/${studentId}/assessment`)}
            >
              {t('directory.studentDetail.tabs.assessment')}
            </button>
            <button type="button" className="ds-btn ds-btn-primary tsd-act-primary" onClick={openMessagePanel}>
              <MessageSquare aria-hidden="true" />
              {t('teacher.studentDetail.messageStudent')}
            </button>
          </>
        )}
      />

      <div className={`page-grid tsd-layout${msgPanelOpen && !compact ? ' page-grid--rail' : ''}`}>
        <div className="ui-stack ui-stack--lg tsd-main">
          {/* When the student was last here and which lesson they are on (GET /crm/students/:id) */}
          <StudentPresenceHeader studentId={studentId} courseId={courseId} />

          {/* Overview */}
          <div className="kpi-row tsd-kpis">
            <MetricCard
              title={t('teacher.studentDetail.overallProgress')}
              value={formatPercent(student.overallProgress || 0)}
              icon={<Gauge />}
              tone="success"
            />
            <MetricCard
              title={t('teacher.studentDetail.quizAverage')}
              value={formatPercent(student.avgQuizScore || 0)}
              icon={<ClipboardCheck />}
              tone="purple"
              change={t('teacher.studentDetail.basedOnAttempts', { count: student.quizAttempts?.length || 0 })}
            />
            <MetricCard
              title={t('teacher.studentDetail.lessonsCompleted')}
              value={`${student.lessonsCompleted || 0} / ${student.totalLessons || 0}`}
              icon={<BookOpenCheck />}
              tone="teal"
            />
          </div>

          {/* Charts: how far through each lesson, and the latest score per quiz (only with enough points) */}
          {(showLessonChart || showQuizChart) && (
            <div className="tsd-charts">
              {showLessonChart && (
                <section className="section-card chart-section">
                  <div className="section-head">
                    <h2 className="section-head__title">{t('teacher.studentDetail.lessonCompletionProgress')}</h2>
                  </div>
                  <ChartFrame data={progressData} minPoints={1}>
                    <BarChart data={progressData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                      <CartesianGrid {...GRID_PROPS} />
                      <XAxis dataKey="n" {...X_AXIS_PROPS} />
                      <YAxis domain={[0, 100]} {...Y_AXIS_PROPS} width={44} tickFormatter={percentTick} />
                      <Tooltip
                        cursor={BAR_CURSOR_PROPS}
                        content={(
                          <ChartTooltip
                            valueFormatter={percentTick}
                            labelFormatter={(n) => progressData[n - 1]?.title || n}
                          />
                        )}
                      />
                      <Bar dataKey="progress" name={t('teacher.studentDetail.completionPercent')} {...barProps(0)} />
                    </BarChart>
                  </ChartFrame>
                </section>
              )}

              {showQuizChart && (
                <section className="section-card chart-section">
                  <div className="section-head">
                    <h2 className="section-head__title">{t('teacher.studentDetail.quizPerformance')}</h2>
                  </div>
                  <ChartFrame data={quizData} minPoints={1}>
                    <BarChart data={quizData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                      <CartesianGrid {...GRID_PROPS} />
                      <XAxis
                        dataKey="quiz"
                        {...X_AXIS_PROPS}
                        // every quiz is named while there is room for it; many bars thin their labels out
                        interval={quizData.length <= 5 ? 0 : 'preserveEnd'}
                        tickFormatter={quizTick(phone ? 8 : 10)}
                      />
                      <YAxis domain={[0, 100]} {...Y_AXIS_PROPS} width={44} tickFormatter={percentTick} />
                      <Tooltip
                        cursor={BAR_CURSOR_PROPS}
                        content={<ChartTooltip valueFormatter={percentTick} />}
                      />
                      <Bar dataKey="score" name={t('teacher.studentDetail.scorePercent')} {...barProps(1)} />
                    </BarChart>
                  </ChartFrame>
                </section>
              )}
            </div>
          )}

          {/* Lessons progress */}
          <section className="lessons-section">
            <div className="section-head">
              <h2 className="section-head__title">{t('teacher.studentDetail.lessonsProgress')}</h2>
            </div>
            {lessonsData.length === 0 ? (
              <PageEmptyState variant="docs" size="sm" title={t('teacher.studentDetail.noLessonsData')} />
            ) : (
              <TableCard aria-label={t('teacher.studentDetail.lessonsProgress')} tall={lessonsData.length > 12}>
                <table className="ui-table ui-table--stack tsd-table">
                  <thead>
                    <tr>
                      <th>{t('teacher.studentDetail.colLesson')}</th>
                      <th>{t('teacher.studentDetail.colProgress')}</th>
                      <th>{t('teacher.studentDetail.colStatus')}</th>
                      <th>{t('teacher.studentDetail.colDate')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lessonsData.map((lesson, idx) => {
                      const percent = Math.round(lesson.completionPercent || (lesson.completed ? 100 : 0));
                      const title = lesson.title || t('teacher.studentDetail.lessonFallbackTitle', { number: idx + 1 });
                      return (
                        <tr key={lesson.lessonId || idx}>
                          <td className="lesson-title">{title}</td>
                          <td data-label={t('teacher.studentDetail.colProgress')}>
                            <div className="tsd-progress">
                              <ProgressBar
                                value={percent}
                                label={`${title}: ${t('teacher.studentDetail.colProgress')}`}
                                className="tsd-progress__bar"
                              />
                              <span className="tsd-progress__text">{formatPercent(Math.min(100, Math.max(0, percent)))}</span>
                            </div>
                          </td>
                          <td data-label={t('teacher.studentDetail.colStatus')}>
                            {lesson.completed ? (
                              <StatusBadge tone="success">{t('teacher.studentDetail.completed')}</StatusBadge>
                            ) : !lesson.started ? (
                              <StatusBadge tone="neutral">{t('teacher.studentDetail.notStarted')}</StatusBadge>
                            ) : (
                              <StatusBadge tone="info">{t('teacher.studentDetail.inProgress')}</StatusBadge>
                            )}
                          </td>
                          <td data-label={t('teacher.studentDetail.colDate')} className="lesson-date">
                            {lesson.completedAt ? formatDate(lesson.completedAt, lang) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableCard>
            )}
          </section>

          {/* Quiz attempts */}
          {student.quizAttempts && student.quizAttempts.length > 0 && (
            <section className="quiz-attempts-section">
              <div className="section-head">
                <h2 className="section-head__title">{t('teacher.studentDetail.quizAttempts')}</h2>
              </div>
              <div className="section-card tsd-attempts ui-list--feed">
                {student.quizAttempts.map((attempt, idx) => {
                  const scorePercent = attempt.totalScore > 0
                    ? Math.round((attempt.score / attempt.totalScore) * 100)
                    : 0;
                  const outcome = attemptOutcome(attempt, t);
                  return (
                    // M-2: fallback key when the attempt has no id
                    <div key={attempt.attemptId ?? idx} className="ui-list-row quiz-attempt-item">
                      <span className="ui-icon-chip ui-icon-chip--md" data-tone={outcome.tone} aria-hidden="true">
                        <ClipboardCheck />
                      </span>
                      <div className="ui-list-row__body">
                        <div className="attempt-title">{attempt.title || t('teacher.studentDetail.quizFallback')}</div>
                        <div className="attempt-details">
                          <span className="detail-badge ds-badge ds-badge-neutral">
                            {t('teacher.studentDetail.correctOf', { correct: attempt.correctAnswers, total: attempt.totalQuestions })}
                          </span>
                          <span className={`detail-badge ds-badge ds-badge-${outcome.tone}`}>
                            {outcome.label}
                          </span>
                          <span className="detail-date">
                            {attempt.attemptedAt ? formatDateTime(attempt.attemptedAt, { lang, year: 'always' }) : '—'}
                          </span>
                        </div>
                      </div>
                      <div className="attempt-score">
                        {attempt.score}/{attempt.totalScore} ({formatPercent(scorePercent)})
                      </div>
                      <button
                        type="button"
                        className="ds-btn ds-btn-secondary ds-btn-sm btn-feedback"
                        onClick={() => navigate({
                          pathname: `/teacher/quiz-feedback/${attempt.attemptId}`,
                          // Context also travels in the query string (not just location.state)
                          // so the feedback page survives a refresh/bookmark/shared link.
                          search: `?courseId=${encodeURIComponent(courseId)}&studentId=${encodeURIComponent(studentId)}&studentName=${encodeURIComponent(displayName || '')}`
                        }, {
                          state: {
                            courseId,
                            studentId,
                            studentName: displayName
                          }
                        })}
                      >
                        {attempt.hasTeacherFeedback ? t('teacher.studentDetail.editFeedback') : t('teacher.studentDetail.provideFeedback')}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        {/* Direct message panel: beside the page on a wide screen */}
        {msgPanelOpen && !compact && (
          <aside className="col-rail">
            <section className="section-card message-panel">
              <div className="section-head">
                <h2 className="section-head__title">{panelTitle}</h2>
                <button
                  type="button"
                  className="ui-icon-btn btn-close-panel"
                  aria-label={t('teacher.studentDetail.closeMessagePanel')}
                  onClick={closeMessagePanel}
                >
                  <X aria-hidden="true" />
                </button>
              </div>
              {conversation}
            </section>
          </aside>
        )}
      </div>

      {/* ...and a sheet over the page below 1024px (a bottom sheet on a phone) */}
      <Modal
        open={msgPanelOpen && compact}
        onClose={closeMessagePanel}
        title={panelTitle}
        size="md"
        className="tsd-chat"
      >
        <div className="message-panel">{conversation}</div>
      </Modal>
    </div>
  );
}

export default StudentDetail;
